import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NXAddOrder, NXAnalytics, NXBagDollar, NXBook, NXCreditCard, NXDollarCircle2, NXHandShake, NXMail, NXManageOrder, NXPen, NXProductBag, NXRocket, NXSecurity, NXServerHealth, NXShipping, NXSMSNotification, NXWrench } from '../icons';
import CreateOrderForm from '../components/orders/create/CreateOrderForm';

const Dashboard: React.FC = () => {
  const [openCreateOrder, setOpenCreateOrder] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const navigate = useNavigate();

  const steps = [
    { title: 'Create Products', description: 'Build a product catalog with detailed descriptions.', icon: <NXProductBag className="h-[50px] w-[50px]"/>, onClick: () => { navigate('/dashboard/products/add-new-product') } },
    { title: 'Analytics', description: "Utilize advanced tools to gain deeper insights into your store's data.", icon: <NXAnalytics className="h-[50px] w-[50px]"/>, onClick: () => { navigate('/dashboard/analytics') } },
    { title: 'Personalize Your Store', description: 'Choose a theme and add your logo to reflect your brand.', icon: <NXPen className="h-[50px] w-[50px] stroke-primary-500"/>, onClick: () => { navigate('/dashboard/settings/') } },
    { title: 'Add Your Store Info', description: 'Enter store details and enable SSL protection for security.', icon: <NXSecurity className="h-[50px] w-[50px] stroke-primary-500"/>, onClick: () => { navigate('/dashboard/settings/') } },
    { title: 'Set Up Shipping', description: 'Choose shipping methods and define delivery zones.', icon: <NXShipping className="h-[50px] w-[50px] stroke-primary-500"/>, onClick: () => { navigate('/dashboard/settings/shipping') } },
    { title: 'Set Up Payments', description: 'Select payment gateways to offer seamless transactions.', icon: <NXCreditCard className="h-[50px] w-[50px] stroke-primary-500"/>, onClick: () => { navigate('/dashboard/settings/') } },
    { title: 'Set Up Taxes', description: 'Configure tax rates to comply with local regulations.', icon: <NXBagDollar className="h-[50px] w-[50px] stroke-primary-500"/>, onClick: () => { navigate('/dashboard/settings/tax-class') } },
    { title: 'Set Up Email Accounts', description: 'Send notifications and communicate with customers.', icon: <NXMail className="h-[50px] w-[50px] stroke-primary-500"/>, onClick: () => { console.log('Analytics clicked'); } },
    { title: 'Launch Your Store', description: 'Go live and start selling to customers worldwide.', icon: <NXRocket className="h-[50px] w-[50px] stroke-primary-500"/>, onClick: () => { console.log('Analytics clicked'); } },
    { title: 'Engage with Customers', description: 'Interact with your customers to build loyalty.', icon: <NXHandShake className="h-[50px] w-[50px] stroke-primary-500"/>, onClick: () => { console.log('Analytics clicked'); } },
    { title: 'Manage Orders', description: 'Allow customers to track their orders in real-time.', icon: <NXManageOrder className="h-[50px] w-[50px] stroke-primary-500"/>, onClick: () => { console.log('Analytics clicked'); } },
    { title: 'Create Orders', description: 'Allow customers to track their orders in real-time.', icon: <NXAddOrder className="h-[50px] w-[50px] stroke-primary-500"/>, onClick: () => { setOpenCreateOrder(true) } },
    { title: 'Set Up SMS Notifications', description: 'Enable SMS alerts for order updates and marketing.', icon: <NXSMSNotification className="h-[50px] w-[50px] stroke-primary-500"/>, onClick: () => { console.log('Analytics clicked'); } },
    { title: 'Maintain Payment Systems', description: 'Regularly monitor and update your payment gateways.', icon: <NXDollarCircle2 className="h-[50px] w-[50px] stroke-primary-500"/>, onClick: () => { console.log('Analytics clicked'); } },
    { title: 'Run Store Maintenance', description: 'Perform routine maintenance to keep your store running smoothly.', icon: <NXWrench className="h-[50px] w-[50px] stroke-primary-500"/>, onClick: () => { console.log('Analytics clicked'); } },
    { title: 'Monitor Server Health', description: 'Check for server errors and optimize performance.', icon: <NXServerHealth className="h-[50px] w-[50px] stroke-primary-500"/>, onClick: () => { console.log('Analytics clicked'); } },
    { title: "Documentation", description: "Learn more about Salesbotics, how to operate, manage, maintain, and enhance it in the Salesbotics documentation.", icon: <NXBook className="h-[50px] w-[50px] stroke-primary-500"/>, onClick: () => { window.open('https://docs.salesbotics.io/', '_blank'); } }
  ];

  const newsFeed = [
    { title: 'Payment Plugin', description: 'A new payment plugin for your store is now available.', link: 'Download the plugin' },
    { title: 'Recommended Hosting', description: 'Everleap cloud hosting offers free migration services.', link: 'Learn more' },
    { title: 'CMS Update Available', description: 'Version 2.1 of Salesbotics is available for download.', link: 'Update now' },
  ];

  // Animation for cards on mount
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Animated Icon wrapper
  const AnimatedIcon: React.FC<{ children: React.ReactNode, isHovered: boolean }> = ({ children, isHovered }) => {
    return (
      <div className={`text-5xl mr-4 transform transition-all duration-500 ${isHovered ? 'scale-110 text-primary-600' : 'text-primary-500'}`}>
        <div className={`transition-all duration-500 ${isHovered ? 'animate-pulse' : ''}`}>
          {children}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-gray-50 to-primary-50 space-y-8 font-nunito">
      {/* Welcome Section */}
      <div className="relative bg-white shadow-xl rounded-2xl p-8 mb-8 transition-all duration-500 ease-in-out border border-gray-100 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute -right-16 -top-16 w-32 h-32 bg-primary-100 rounded-full opacity-50"></div>
        <div className="absolute -left-16 -bottom-16 w-48 h-48 bg-secondary-100 rounded-full opacity-40"></div>
        
        {/* Close and Minimize Buttons */}
        <div className="absolute top-4 right-4 flex space-x-3 z-10">
          <button
            className="w-6 h-6 rounded-full bg-primary-500 text-white font-bold text-xs flex items-center justify-center hover:bg-primary-600 transition-colors shadow-md"
            aria-label="Close"
          >
            ✕
          </button>
          <button
            className="w-6 h-6 rounded-full bg-secondary-300 text-white font-bold text-xs flex items-center justify-center hover:bg-secondary-400 transition-colors shadow-md"
            aria-label="Minimize"
          >
            –
          </button>
        </div>

        <h1 className="text-3xl font-nunito-h1 text-base-600 mb-6 relative z-10 flex items-center">
          Welcome to <span className="text-primary-600 ml-2 font-bold">Salesbotics!</span>
          <div className="ml-2 w-2 h-4 bg-primary-500 animate-pulse"></div>
        </h1>
        
        <div className={`grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 transition-opacity duration-700 ease-in-out ${mounted ? 'opacity-100' : 'opacity-0'}`}>
          {steps.map((step, index) => (
            <div
              key={index}
              onClick={step.onClick}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
              className={`flex items-start p-5 rounded-xl shadow-md transition-all duration-300 cursor-pointer transform ${hoveredCard === index ? 'scale-105 bg-white' : 'bg-secondary-50 hover:shadow-lg'}`}
              style={{ 
                animationDelay: `${index * 0.05}s`,
                transitionDelay: `${index * 0.03}s`
              }}
            >
              <AnimatedIcon isHovered={hoveredCard === index}>
                {step.icon}
              </AnimatedIcon>
              <div>
                <h3 className={`text-xl font-nunito-h2 mb-2 transition-colors duration-300 ${hoveredCard === index ? 'text-primary-600' : 'text-base-500'}`}>
                  {step.title}
                </h3>
                <p className="text-base-400 font-lato-body text-sm">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {openCreateOrder && <CreateOrderForm isOpen={openCreateOrder} onClose={()=>setOpenCreateOrder(false)}/>}
    </div>
  );
};

export default Dashboard;